import "./SpaceCountdownPanel.scss";
import React from "react";
import { CountdownParts } from "App/common/useCountdown";
import { AvatarGroupUser } from "App/common/UserAvatar/UserAvatarGroup";
import { BUTTON_SIZE, BUTTON_THEME, Button } from "App/core/Button";
import { useTheme } from "App/common/ThemeProvider";
import { ReactComponent as AddSVG } from "App/common/icons-v2/add.svg";
import { ReactComponent as CheckSVG } from "App/common/icons-v2/check.svg";
import { DownloadAppButton } from "App/common/DownloadAppButton";
import { useViewport } from "App/common/useViewport";
import { SpaceCTA, SpaceStatus } from "./SpaceCTA";

const pad = (value: number) => String(value).padStart(2, "0");

const CountdownSegment = ({
  value,
  label,
}: {
  value: number;
  label: string;
}) => (
  <div className="space-countdown-segment">
    <span className="space-countdown-value">{pad(value)}</span>
    <span className="space-countdown-label">{label}</span>
  </div>
);

export const CountdownTimer = ({
  countdown,
}: {
  countdown: CountdownParts;
}) => (
  <div className="space-countdown">
    <CountdownSegment value={countdown.days} label="days" />
    <span className="space-countdown-separator">:</span>
    <CountdownSegment value={countdown.hours} label="hours" />
    <span className="space-countdown-separator">:</span>
    <CountdownSegment value={countdown.minutes} label="minutes" />
    <span className="space-countdown-separator">:</span>
    <CountdownSegment value={countdown.seconds} label="seconds" />
  </div>
);

/**
 * The launch-countdown lockup for the space page and the guest "join to view"
 * preview. Builds the status (the live countdown timer, or a LIVE / PRIVATE
 * pill once open, driven by the share link) and the membership CTA, then hands
 * them to the presentational `SpaceCTA`. The app-prompt dialog uses `SpaceCTA`
 * directly instead — this component is only for the space surfaces.
 */
export const SpaceCountdownPanel = ({
  countdown,
  members,
  memberCount,
  className,
  joined,
  canManage,
  onJoin,
  isJoining,
  isLinkShared,
  allowJoinOnMobileWeb,
}: {
  countdown: CountdownParts;
  members?: AvatarGroupUser[];
  memberCount?: number;
  className?: string;
  /**
   * Viewer's membership: `true` → non-clickable "You're In" (or "Download the
   * App" on mobile web), `false` → "Join Space" button, `undefined` → not yet
   * known (render no CTA, so a member doesn't briefly flash "Join Space" before
   * auth resolves).
   */
  joined?: boolean;
  /** Viewer manages the space → show no CTA (they already have full access). */
  canManage?: boolean;
  onJoin?: () => void;
  isJoining?: boolean;
  /**
   * Whether the space's share link grants VIEW (the SpaceNavigation
   * `isSpaceShared` signal). Drives the open status: shared → LIVE (green), off
   * → PRIVATE (red).
   */
  isLinkShared?: boolean;
  /**
   * Mobile web (below M) normally pushes everyone to the app. The guest join
   * gate (JoinSpaceConfirmation) opts out for non-members: joining must work on
   * web there, since the viewer can't see the space at all until they join —
   * "Download the App" would dead-end the whole flow.
   */
  allowJoinOnMobileWeb?: boolean;
}) => {
  const { theme } = useTheme();
  const { isMobile } = useViewport();
  const showTimer = countdown.isActive;

  const joinButton = (
    <Button
      theme={BUTTON_THEME.CTA}
      fullWidth
      loading={isJoining}
      disabled={isJoining}
      startIcon={<AddSVG />}
      onClick={onJoin}
    >
      Join Space
    </Button>
  );

  const downloadAppButton = <DownloadAppButton fullWidth />;

  // Not a real button — mimics the button's shape (reuses `.highnote-button`
  // plus the size/theme data attributes for height, padding, radius and fill)
  // but is a plain, non-interactive element.
  const youreInBadge = (
    <div
      className="highnote-button full-width space-countdown-youre-in"
      data-size={BUTTON_SIZE.MEDIUM}
      data-button-theme={BUTTON_THEME.FLAT_BLACK}
      data-theme={theme}
      aria-disabled
    >
      <span className="start-icon">
        <CheckSVG />
      </span>
      You&rsquo;re In !
    </div>
  );

  const cta = (() => {
    // Below M, mobile web is push-to-app for everyone — even owners can't manage
    // or comment here — so the CTA is always "Download the App".
    if (isMobile) {
      // ...unless membership is still resolving, to avoid a flash.
      if (joined === undefined) return null;
      // The join gate keeps the join flow on web (see prop docs).
      if (allowJoinOnMobileWeb && !joined) return joinButton;
      return downloadAppButton;
    }
    // Above M (full desktop access):
    // Membership not yet known — render nothing rather than flash the wrong CTA.
    if (joined === undefined) return null;
    // Managers already have full access, so the panel is purely informational.
    if (canManage) return null;
    if (!joined) return joinButton;
    // Members get a status confirmation.
    return youreInBadge;
  })();

  // While counting down, the timer fills the status slot. Once open, a status
  // pill takes over: LIVE (link shared) or PRIVATE (link off).
  const status = showTimer ? (
    <CountdownTimer countdown={countdown} />
  ) : (
    <SpaceStatus
      label={isLinkShared ? "LIVE" : "PRIVATE"}
      dot={isLinkShared ? "green" : "red"}
    />
  );

  return (
    <SpaceCTA
      className={className}
      status={status}
      members={members}
      memberCount={memberCount}
      cta={cta}
    />
  );
};
